import { TOEFLSection } from './types';

/**
 * Section 介绍文案接口
 */
export interface SectionIntro {
  sectionId: string;
  title: string;
  description: string;
  tips?: string[];
  duration?: number;
}

/**
 * TOEFL 阅读理解介绍
 */
const TOEFL_READING_INTRO: SectionIntro = {
  sectionId: TOEFLSection.READING,
  title: '阅读 (Reading)',
  description: 'TOEFL 阅读部分测试您阅读和理解学术材料的能力。包含3-4篇文章，每篇约700词，涵盖科学、历史、艺术、社会科学等学术领域，共30-40道题目。',
  duration: 54,
  tips: [
    '先快速浏览文章标题、段落首句，了解文章主题和结构',
    '注意文章的逻辑结构，如因果关系、对比关系等',
    '使用略读技巧快速定位关键信息',
    '使用扫读技巧查找具体细节',
    '注意同义词替换，答案可能不是原文原词',
    '合理分配时间，每篇文章约18分钟',
    '遇到不认识的词汇不要纠结，通过上下文推断词义',
    '注意文章中的例子和例证，它们往往支撑重要观点',
  ],
};

/**
 * TOEFL 听力理解介绍
 */
const TOEFL_LISTENING_INTRO: SectionIntro = {
  sectionId: TOEFLSection.LISTENING,
  title: '听力 (Listening)',
  description: 'TOEFL 听力部分测试您理解英语口语的能力。包含4-6个讲座（课堂演讲）和2-3个对话（校园对话），每个讲座约5分钟，对话约3分钟，共28-39道题目。',
  duration: 41,
  tips: [
    '在音频播放前，先快速浏览题目，了解需要关注的信息',
    '注意听力材料中的关键词和转折词',
    '注意说话者的态度和观点，这往往是题目的重点',
    '做好笔记，记录关键信息、例子、时间、地点等',
    '注意区分主旨题和细节题，主旨题往往在开头或结尾',
    '注意讲座中的例子和例证，它们通常用于支撑观点',
    '对话题注意人物之间的关系和对话目的',
    '合理分配时间，不要在单个题目上花费过多时间',
  ],
};

/**
 * TOEFL 口语介绍
 */
const TOEFL_SPEAKING_INTRO: SectionIntro = {
  sectionId: TOEFLSection.SPEAKING,
  title: '口语 (Speaking)',
  description: 'TOEFL 口语部分包含4道题目，测试您的英语口语表达能力。第1题是独立口语，表达个人观点；第2-4题是综合口语，需要结合阅读或听力材料进行回答。',
  duration: 17,
  tips: [
    '第1题：准备15秒，回答45秒，直接回答问题，给出具体例子',
    '第2-4题：先快速阅读或听材料，记录关键信息',
    '回答时要结构清晰，可以使用"首先、其次、最后"等连接词',
    '注意控制时间，确保在规定时间内完成回答',
    '发音清晰，语速适中，不要过快',
    '使用多样的词汇和语法结构，展现语言能力',
    '练习时录音回听，自我改进发音和表达',
    '即使紧张也不要停止，保持流畅',
    '综合题要准确复述阅读和听力材料中的关键信息',
  ],
};

/**
 * TOEFL 写作介绍
 */
const TOEFL_WRITING_INTRO: SectionIntro = {
  sectionId: TOEFLSection.WRITING,
  title: '写作 (Writing)',
  description: 'TOEFL 写作部分包含2道题目。第1题是综合写作（20分钟），需要阅读一篇文章、听一段讲座，然后写一篇总结；第2题是独立写作（30分钟），针对某个话题发表观点。',
  duration: 50,
  tips: [
    '第1题（综合写作）：阅读时做笔记，记录文章主要观点',
    '第1题：听讲座时注意教授对文章观点的反驳或补充',
    '第1题：写作时要总结讲座观点，而不是个人观点',
    '第1题：保持客观，准确复述阅读和听力材料的内容',
    '第2题（独立写作）：留2-3分钟规划文章结构',
    '第2题：确保有明确的论点和充分的论证',
    '第2题：使用具体的例子支持论点',
    '使用恰当的连接词，使文章逻辑清晰',
    '使用多样的句式和词汇，展现语言能力',
    '注意字数，综合写作通常150-225词，独立写作通常300词以上',
    '留2-3分钟检查语法和拼写错误',
  ],
};

/**
 * TOEFL Section 介绍文案映射
 */
export const TOEFL_SECTION_INTROS: Record<string, SectionIntro> = {
  [TOEFLSection.READING]: TOEFL_READING_INTRO,
  [TOEFLSection.LISTENING]: TOEFL_LISTENING_INTRO,
  [TOEFLSection.SPEAKING]: TOEFL_SPEAKING_INTRO,
  [TOEFLSection.WRITING]: TOEFL_WRITING_INTRO,
};

/**
 * 获取 TOEFL Section 介绍文案
 */
export function getTOEFLSectionIntro(sectionId: string): SectionIntro | null {
  return TOEFL_SECTION_INTROS[sectionId] || null;
}
